import React, { useState, useMemo } from 'react'
import shortid from 'shortid'
import moment from 'moment'

import Input from './input'
import Button, { ButtonRow } from './button'
import { GeneratorFields } from '../api-client/api-types'
import styles from '../component-styles/campaign-code-generator.module.scss'
import { paginateData, sortData, getItemByKeyValue } from '../helpers'
// import TopScrollbar from './top-scrollbar'
import Pagination from './pagination'
import { SelectBoxSimple } from './select-box'
import OrderArrow from './order-arrow'
import { dateFormatShort } from '../core/variables'

interface CampaignCodesPreviewTableProps {
  form: any
  codes: any
  url?: string[]
  showIds?: string[]
}
export default function CampaignCodePreviewTable({
  form,
  codes,
  url = [],
  showIds = [],
}: CampaignCodesPreviewTableProps): React.ReactElement | null {
  const [previewCodes, setShowPreviewCodes] = useState<any>(codes)
  const [activePage, setActivePage] = useState(1)
  const [pages, setPages] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [allChecked, setAllChecked] = useState(true)
  const [sortKey, setSortKey] = useState('t')
  const [orderAsc, setOrderAsc] = useState(false)
  const cellsOrder = useMemo(() => {
    if (form) {
      return form.paramDefs.map((item: GeneratorFields) => item.fieldID || '')
    }
    return []
  }, [form])

  const setSortOrder = (key: string): void => {
    if (key === sortKey) {
      setOrderAsc(!orderAsc)
    } else {
      setSortKey(key)
      setOrderAsc(true)
    }
    // Go to the start of pagination
    setActivePage(1)
  }

  const orderedData = useMemo(() => {
    let fn
    // eslint-disable-next-line
    // debugger
    if (sortKey.search(/[0-9]{1,9}-/gi) === 0) {
      const index = sortKey.split('-')[0]
      fn = (row) => {
        return row.cDef[index][1] || ''
      }
    }
    const useCodes = previewCodes.filter(
      (code) => showIds.length === 0 || showIds.indexOf(code.cID) !== -1,
    )
    const filtered = sortData([...useCodes], sortKey, orderAsc, fn)
    setPages(Math.ceil(filtered.length / rowsPerPage))
    return paginateData(filtered, rowsPerPage)
  }, [sortKey, orderAsc, previewCodes, rowsPerPage])

  if (!form || previewCodes.length === 0) {
    return <></>
  }
  return (
    <>
      <div className={styles.tableContainer}>
        <table className={styles.compactTable}>
          <thead>
            <tr>
              <th className={styles.checkboxCell}>
                <Input
                  type="checkbox"
                  id="allNone"
                  name="allNone"
                  checked={allChecked}
                  className={styles.selectItem}
                  label=" "
                  onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                    const { checked } = e.target as HTMLInputElement
                    setAllChecked(checked)
                    const setCodes = previewCodes.map((item) => {
                      return {
                        ...item,
                        selected: checked,
                      }
                    })
                    setShowPreviewCodes(setCodes)
                  }}
                />
              </th>
              <th className={styles.dateCell}>
                <Button
                  className={styles.headerButton}
                  onClick={(): void => {
                    setSortOrder('t')
                  }}
                  type="none"
                >
                  Created
                  <OrderArrow
                    className={styles.orderArrow}
                    currentKey="t"
                    sortKey={sortKey}
                    orderAsc={orderAsc}
                  />
                </Button>
              </th>
              <th className={styles.codeColumn}>
                <Button
                  className={styles.headerButton}
                  onClick={(): void => {
                    setSortOrder('c')
                  }}
                  type="none"
                >
                  Landing page with campaign code
                  <OrderArrow
                    className={styles.orderArrow}
                    currentKey="c"
                    sortKey={sortKey}
                    orderAsc={orderAsc}
                  />
                </Button>
              </th>
              {form &&
                form.paramDefs.map(
                  (
                    field: GeneratorFields,
                    index: number,
                  ): React.ReactElement => {
                    return (
                      <th
                        key={shortid.generate()}
                        className={styles.otherColumn}
                      >
                        <Button
                          className={styles.headerButton}
                          onClick={(): void => {
                            setSortOrder(`${index}-${field.fieldName}`)
                          }}
                          type="none"
                        >
                          {field.fieldName}
                          <OrderArrow
                            className={styles.orderArrow}
                            currentKey={`${index}-${field.fieldName}`}
                            sortKey={sortKey}
                            orderAsc={orderAsc}
                          />
                        </Button>
                      </th>
                    )
                  },
                )}
            </tr>
          </thead>
          <tbody>
            {orderedData.length > 0 &&
              orderedData[activePage - 1] &&
              orderedData[activePage - 1].map(
                (field: any): React.ReactElement => {
                  const dateObject = moment(field.t)
                  return (
                    <tr key={shortid.generate()} className={styles.otherColumn}>
                      <td className={styles.checkboxCell}>
                        <Input
                          type="checkbox"
                          id={shortid.generate()}
                          name={shortid.generate()}
                          checked={field.selected}
                          className={styles.selectItem}
                          label=" "
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ): void => {
                            const { checked } = e.target as HTMLInputElement
                            const setCodes = previewCodes.map((item) => {
                              if (field.code === item.code) {
                                return {
                                  ...item,
                                  selected: checked,
                                }
                              }
                              return item
                            })
                            setShowPreviewCodes(setCodes)
                          }}
                        />
                      </td>
                      <td>
                        <span className={styles.createdWrapper}>
                          <span className={styles.created}>
                            <span className={styles.date}>
                              {dateObject.format(dateFormatShort)}
                            </span>
                            <span className={styles.timeSince}>
                              {dateObject.format('HH:mm')}
                            </span>
                          </span>
                          <span className={styles.user}>
                            {field.e || 'Account user'}
                          </span>
                        </span>
                      </td>
                      <td>{field.c}</td>
                      {cellsOrder.map(
                        (fieldID): React.ReactElement => {
                          const paramDef = getItemByKeyValue(
                            field.cDef,
                            'fID',
                            fieldID,
                          )

                          const useValue = paramDef !== -1 ? paramDef.n : ''
                          return (
                            <td
                              key={`td-${fieldID}`}
                              className={styles.containsInput}
                            >
                              {useValue}
                            </td>
                          )
                        },
                      )}
                    </tr>
                  )
                },
              )}
          </tbody>
        </table>
      </div>
      <ButtonRow className={styles.paginationRow}>
        <Pagination
          pages={pages}
          activePage={activePage}
          onChange={(index: number) => {
            setActivePage(index)
          }}
        />
        <span className={styles.rowsLabelWrapper}>
          <span className={styles.rowsLabel}>Show</span>
          <SelectBoxSimple
            name="rows-per-page"
            value={rowsPerPage}
            onChange={(rows) => {
              setRowsPerPage(rows)
              setActivePage(1)
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="150">150</option>
          </SelectBoxSimple>
        </span>
      </ButtonRow>
    </>
  )
}
